import { Sparkle } from "@phosphor-icons/react";
import { FOCUS_OPTIONS } from "../../lib/focusBlueprints";

const GOAL_PRESETS = [
  { id: "acft", label: "Improve ACFT Score", description: "Balanced strength + SPT power + SDC conditioning + 2MR run. Prioritizes the six ACFT events." },
  { id: "2mr", label: "Faster 2-Mile Run", description: "Aerobic base + threshold + VO2 intervals. Strength work that supports running economy." },
  { id: "ruck", label: "Faster Ruck (Distance & Pace)", description: "Posterior-chain strength + heavy rucks + tempo rucks. Built for load-bearing endurance." },
  { id: "strength", label: "Build General Strength", description: "Compound lifts at 70-85% 1RM. Hypertrophy and accessory work to support the big lifts." },
  { id: "hypertrophy", label: "Build Muscle (Hypertrophy)", description: "Volume-focused (8-15 reps), split across upper/lower. More accessory work, less heavy percentage work." },
  { id: "fatloss", label: "Lose Fat, Keep Strength", description: "Maintain compound lifts with moderate volume; add conditioning finishers and metcons. Calorie control is on you." },
  { id: "general", label: "General Fitness Improvement", description: "Balanced strength, endurance, mobility. No single bias. Great if you're not sure." },
  { id: "custom", label: "Custom (enter your own goal)", description: "Free-text. The AI will design around what you describe." },
];

// Grouped to match the exact equipment values in /app/backend/exercises_data.py.
// "Sled, Kettlebells" is a multi-equipment value used for the Sprint-Drag-Carry.
const EQUIPMENT_GROUPS = [
  {
    label: "Bodyweight & Bands",
    items: ["None", "Resistance Band", "Pull-up Bar", "Dip Bars", "Box"],
  },
  {
    label: "Free Weights",
    items: ["Dumbbell", "Barbell", "Kettlebell", "Trap Bar", "Sandbag", "Med Ball"],
  },
  {
    label: "Machines & Cables",
    items: ["Cable", "Machine", "Ab Wheel"],
  },
  {
    label: "Cardio Gear",
    items: ["Bike", "Rower", "Ski Erg", "Jump Rope", "Pool"],
  },
  {
    label: "Tactical / Field",
    items: ["Ruck", "Weight Vest", "Sled", "Sled, Kettlebells", "Foam Roller"],
  },
];

function Field({ label, children }) {
  return (
    <div>
      <div className="label-mono mb-1">{label}</div>
      {children}
    </div>
  );
}

export default function AIForm({ form, setForm, loading, err, onGenerate }) {
  const toggleEq = (eq) => {
    setForm((f) => ({
      ...f,
      equipment: f.equipment.includes(eq) ? f.equipment.filter((x) => x !== eq) : [...f.equipment, eq],
    }));
  };

  return (
    <div className="border border-border bg-card p-5 space-y-5 h-fit">
      <Field label="Goal">
        <select
          value={form.goal_id || "general"}
          onChange={(e) => {
            const goalId = e.target.value;
            const preset = GOAL_PRESETS.find((g) => g.id === goalId);
            setForm({
              ...form,
              goal_id: goalId,
              goal: goalId === "custom" ? (form.goal || "") : (preset?.label || form.goal),
            });
          }}
          className="w-full bg-secondary border border-border px-3 py-2"
          data-testid="ai-goal-select"
        >
          {GOAL_PRESETS.map((g) => (
            <option key={g.id} value={g.id}>{g.label}</option>
          ))}
        </select>
        <div className="text-xs text-muted-foreground mt-1.5 leading-relaxed">
          {GOAL_PRESETS.find((g) => g.id === (form.goal_id || "general"))?.description}
        </div>
        {form.goal_id === "custom" && (
          <input
            value={form.goal || ""}
            onChange={(e) => setForm({ ...form, goal: e.target.value })}
            placeholder="Describe your specific goal..."
            className="w-full mt-2 bg-secondary border border-border px-3 py-2 focus:outline-none focus:border-volt"
            data-testid="ai-goal-custom-input"
          />
        )}
      </Field>
      <Field label="Level">
        <select value={form.level} onChange={(e) => setForm({ ...form, level: e.target.value })} className="w-full bg-secondary border border-border px-3 py-2" data-testid="ai-level-select">
          <option>Beginner</option><option>Intermediate</option><option>Advanced</option>
        </select>
      </Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Weeks">
          <input type="number" min="1" max="12" value={form.weeks} onChange={(e) => setForm({ ...form, weeks: parseInt(e.target.value) || 1 })} className="w-full bg-secondary border border-border px-3 py-2" data-testid="ai-weeks-input" />
        </Field>
        <Field label="Days/Week">
          <input
            type="number"
            min="2"
            max="6"
            value={form.days_per_week}
            onChange={(e) => {
              const n = Math.max(2, Math.min(6, parseInt(e.target.value) || 3));
              const existing = form.day_structure || [];
              const next = Array.from({ length: n }, (_, i) => existing[i] || "auto");
              setForm({ ...form, days_per_week: n, day_structure: next });
            }}
            className="w-full bg-secondary border border-border px-3 py-2"
            data-testid="ai-days-input"
          />
        </Field>
      </div>

      <Field label="Weekly Structure (optional)">
        <div className="text-xs text-muted-foreground mb-2">
          Pick a focus for each day, or leave as <span className="font-display text-volt">AUTO</span> to let the AI decide.
        </div>
        <div className="space-y-1" data-testid="ai-week-structure">
          {Array.from({ length: form.days_per_week || 3 }).map((_, i) => {
            const current = (form.day_structure || [])[i] || "auto";
            return (
              <div key={i} className="flex items-center gap-2">
                <span className="label-mono text-muted-foreground w-12 shrink-0">DAY {i + 1}</span>
                <select
                  value={current}
                  onChange={(e) => {
                    const next = [...(form.day_structure || [])];
                    next[i] = e.target.value;
                    setForm({ ...form, day_structure: next });
                  }}
                  className="flex-1 bg-secondary border border-border px-2 py-1.5 text-xs"
                  data-testid={`ai-day-${i}-focus`}
                >
                  <option value="auto">AUTO (AI decides)</option>
                  {FOCUS_OPTIONS.map((opt) => (
                    <option key={opt.id} value={opt.id}>{opt.label}</option>
                  ))}
                </select>
              </div>
            );
          })}
        </div>
      </Field>
      <Field label="Equipment">
        <div className="space-y-3">
          {EQUIPMENT_GROUPS.map((group) => (
            <div key={group.label}>
              <div className="label-mono text-muted-foreground mb-1">// {group.label.toUpperCase()}</div>
              <div className="flex flex-wrap gap-1">
                {group.items.map((eq) => (
                  <button
                    key={eq}
                    onClick={() => toggleEq(eq)}
                    className={`px-2 py-1 text-xs uppercase tracking-widest font-display border ${form.equipment.includes(eq) ? "bg-volt text-black border-volt" : "border-border text-muted-foreground hover:text-foreground"}`}
                    data-testid={`ai-eq-${eq}`}
                  >
                    {eq}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      </Field>
      <Field label="Notes (optional)">
        <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows="3" className="w-full bg-secondary border border-border px-3 py-2 focus:outline-none focus:border-volt" data-testid="ai-notes-input" />
      </Field>

      <Field label="AI Engine">
        <div className="flex gap-1 border border-border p-1" data-testid="ai-mode-toggle">
          <button
            onClick={() => setForm({ ...form, mode: "budget" })}
            data-testid="ai-mode-budget"
            className={`flex-1 px-3 py-2 text-xs uppercase tracking-widest font-display ${(form.mode || "budget") === "budget" ? "bg-volt text-black" : "text-muted-foreground hover:text-foreground"}`}
          >
            Budget
          </button>
          <button
            onClick={() => setForm({ ...form, mode: "premium" })}
            data-testid="ai-mode-premium"
            className={`flex-1 px-3 py-2 text-xs uppercase tracking-widest font-display ${form.mode === "premium" ? "bg-volt text-black" : "text-muted-foreground hover:text-foreground"}`}
          >
            Premium
          </button>
        </div>
        <div className="text-xs text-muted-foreground mt-1.5 leading-relaxed">
          {form.mode === "premium"
            ? "Claude Sonnet 4.5 — highest quality, costs ~10× more."
            : "Gemini 2.5 Flash — fast & ~10× cheaper. Recommended for most plans."}
        </div>
      </Field>

      <button onClick={onGenerate} disabled={loading} className="w-full bg-volt text-black py-3 font-display uppercase tracking-widest font-bold hover:bg-white disabled:opacity-50 inline-flex items-center justify-center gap-2" data-testid="ai-generate-btn">
        <Sparkle size={18} weight="bold" /> {loading ? "Drafting..." : "Generate Plan"}
      </button>
      {err && <div className="border border-destructive text-destructive px-3 py-2 text-sm" data-testid="ai-error">{err}</div>}
    </div>
  );
}
